import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Boxes, TrendingDown, Receipt, ArrowRight, Building2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwa2l0Y2hlbiUyMGNoZWYlMjBidXN5fGVufDB8fHx8MTc4NTQ4NjEzNHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Building2, title: "Multi-branch stock", desc: "Per-branch levels derived from movements — counts can't drift. Transfers with send/receive confirmation." },
  { icon: Receipt, title: "POs with 12% VAT", desc: "Draft → sent → received. Landed cost recorded with net, VAT and gross splits, PHP all the way." },
  { icon: TrendingDown, title: "Wastage control", desc: "Log spoilage, breakage, over-prep and staff meals with peso value lost, by branch." },
];

export default function Landing() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (auth?.membership) navigate("/dashboard");
  }, [auth, navigate]);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-screen-xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-extrabold text-lg tracking-tight">
            <Boxes className="h-6 w-6 text-primary" /> StockPilot PH
          </div>
          <Link to="/auth">
            <Button data-testid="landing-signin-btn" className="rounded-full px-6">Sign in</Button>
          </Link>
        </div>
      </header>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Restaurant kitchen" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/70" />
        </div>
        <div className="relative max-w-screen-xl mx-auto px-6 py-28 sm:py-36">
          <p className="text-xs uppercase tracking-widest text-blue-200 mb-4 font-semibold">Restaurant Inventory Cloud · Philippines</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-3xl leading-tight">
            Every branch. Every kilo. <span className="text-blue-300">Every peso.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-200 max-w-xl">
            Multi-branch inventory and purchasing for Philippine restaurant groups — local units, 12% VAT on costs, Asia/Manila time.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/auth">
              <Button size="lg" data-testid="landing-cta-btn" className="rounded-full px-8 text-base">
                Get started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <section className="max-w-screen-xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="border rounded-xl bg-card p-8 hover:-translate-y-0.5 hover:shadow-sm transition-transform">
              <f.icon className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <footer className="border-t py-8 text-center text-xs text-muted-foreground">
        StockPilot PH — built for Philippine restaurant groups. ₱ · 12% VAT · Asia/Manila
      </footer>
    </div>
  );
}
